import { Box, Flex, Icon, Tooltip } from "@chakra-ui/react";
import React, { useState } from "react";
import { MdCheck, MdClose, MdContentCopy, MdInfo } from "react-icons/md";

import DatetimeWithTooltip from "../../admin/components/DatetimeWithTooltip";
import { copy } from "../../utils/clipboard";
import { IconButton } from "../Buttons";
import { Card, CardProps } from "../Card";

type DescriptionListItem = {
  name: string;
  description: React.ReactNode;
  copyValue?: string | null;
  tooltip?: string;
};

export type DescriptionListProps = {
  data: Array<DescriptionListItem>;
  paddingStyle?: "normal" | "interior-only";
} & CardProps;

export const DescriptionList: React.FC<DescriptionListProps> = ({
  data,
  paddingStyle,
  ...cardProps
}) => {
  return (
    <Card fontSize="sm" width="fit-content" {...cardProps}>
      {data.map(({ name, description, copyValue, tooltip }, index) => {
        if (description === undefined || description === null) {
          return null;
        }
        return (
          <DescriptionItem
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            description={description}
            name={name}
            copyValue={copyValue}
            tooltip={tooltip}
            index={index}
            paddingStyle={paddingStyle}
          />
        );
      })}
    </Card>
  );
};

interface DescriptionItemProps {
  index: number;
  description: React.ReactNode;
  copyValue?: string | null;
  name: string;
  tooltip?: string;
  paddingStyle?: "normal" | "interior-only";
}

const DescriptionItem: React.FC<DescriptionItemProps> = ({
  index,
  description,
  copyValue: copyValueParam,
  name,
  tooltip,
  paddingStyle = "normal",
}) => {
  const copyValue = copyValueParam || description?.toString() || "";
  const canCopy = copyValue !== "" && copyValue !== "[object Object]";
  const [copyState, setCopyState] = useState<"success" | "failure" | "waiting">(
    "waiting"
  );

  const getVariant = (): string => {
    if (copyState === "success") return "successStatic";
    if (copyState === "failure") return "dangerStatic";
    return "none";
  };

  const getIcon = (): JSX.Element => {
    if (copyState === "success") return <MdCheck />;
    if (copyState === "failure") return <MdClose />;
    return <MdContentCopy />;
  };

  const getLabel = (): string => {
    if (copyState === "success") return "Successfully copied!";
    if (copyState === "failure") return "Failed to copy, please try again";
    return "Copy to clipboard";
  };

  let truncatedDescription: string | undefined;
  if (typeof description === "string") {
    if (description.length > 60) {
      truncatedDescription = `${description.slice(0, 60)}…`;
    }
  }

  return (
    <Flex
      key={name}
      py="4"
      borderTop={index === 0 ? "none" : "1px"}
      borderColor="border"
    >
      <Box
        pl={paddingStyle === "normal" ? 6 : 0}
        pr={6}
        fontWeight="bold"
        minWidth="300px"
      >
        {name}
        {tooltip && (
          <Tooltip label={tooltip} placement="top">
            <Box as="span" ml={1} display="inline-flex" verticalAlign="middle">
              <Icon as={MdInfo} color="gray.400" />
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box pr={paddingStyle === "normal" || canCopy ? 6 : 0} pl={6} ml="auto">
        {truncatedDescription ? (
          <Tooltip label={description}>{truncatedDescription}</Tooltip>
        ) : (
          <>{description}</>
        )}
      </Box>
      {canCopy && (
        <Flex flexFlow="row wrap">
          <Tooltip label={getLabel()}>
            <IconButton
              size="xs"
              aria-label="Copy to Clipboard"
              variant={getVariant()}
              icon={getIcon()}
              onClick={() => {
                copy(copyValue);
                if (copyValue) {
                  setCopyState("success");
                } else {
                  setCopyState("failure");
                }
                setTimeout(() => {
                  setCopyState("waiting");
                }, 2500);
              }}
              mr={paddingStyle === "normal" ? 5 : -1}
            />
          </Tooltip>
        </Flex>
      )}
    </Flex>
  );
};

export const dateTimeItemWithTooltip = (
  name: string,
  datetimeString: string | null
): DescriptionListItem => {
  if (!datetimeString) {
    return {
      name,
      description: null,
    };
  }
  return {
    name,
    description: <DatetimeWithTooltip datetimeString={datetimeString} />,
    // strip off the milliseconds and timezone offset
    copyValue: datetimeString?.split(".")[0],
  };
};
